import React from 'react';
import NavBar from '../components/Navbar/NavBar';
import Footer from '../components/Footer';
import { useDocTitle } from '../components/CustomHook';
import { Link } from 'react-router-dom';
import img from '../images/web.svg';

const CustomSoftware = () => {
    useDocTitle('Custom Software Solutions - Claravio Solutions');

    return (
        <>
            <NavBar />
            <div className="bg-gray-50 min-h-screen pt-28">
                {/* Hero Section */}
                <div className=" text-white py-20" style={{ backgroundColor: '#2D3C54' }}>
                    <div className="container mx-auto px-4 text-center">
                        <h1 className="text-4xl lg:text-6xl font-bold mb-4">Custom Software Solutions</h1>
                        <p className="text-xl lg:text-2xl mb-8">Drive operational efficiency with tailored automation solutions</p>
                        <Link to="/contact" className="bg-white text-claravio-blue-900 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition duration-300">
                            Get Started
                        </Link>
                    </div>
                </div>

                {/* Main Content */}
                <div className="container mx-auto px-4 py-16">
                    <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
                        <div>
                            <img src={img} alt="Custom Software Development" className="w-full max-w-md mx-auto" />
                        </div>
                        <div>
                            <h2 className="text-3xl font-bold text-claravio-blue-900 mb-6">Transform Your Operations</h2>
                            <p className="text-lg text-gray-700 mb-6">
                                We specialize in developing custom software solutions that drive operational efficiency. 
                                Our automation experts will quickly develop a customized solution and seamlessly integrate 
                                with your back-office or third-party systems.
                            </p>
                            <p className="text-lg text-gray-700">
                                Every business has unique challenges and processes. Off-the-shelf software often falls short 
                                of meeting specific operational needs. That's where our custom software development expertise 
                                comes in, creating solutions that fit your exact requirements.
                            </p>
                        </div>
                    </div>

                    {/* Services Grid */}
                    <div className="mb-16">
                        <h2 className="text-3xl font-bold text-claravio-blue-900 text-center mb-12">Our Custom Software Services</h2>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                            <div className="bg-white p-6 rounded-lg shadow-lg">
                                <h3 className="text-xl font-semibold text-claravio-blue-900 mb-4">Process Automation</h3>
                                <p className="text-gray-600">Automate repetitive tasks and workflows to increase efficiency and reduce human error.</p>
                            </div>
                            <div className="bg-white p-6 rounded-lg shadow-lg">
                                <h3 className="text-xl font-semibold text-claravio-blue-900 mb-4">System Integration</h3>
                                <p className="text-gray-600">Connect disparate systems to create seamless data flow across your organization.</p>
                            </div>
                            <div className="bg-white p-6 rounded-lg shadow-lg">
                                <h3 className="text-xl font-semibold text-claravio-blue-900 mb-4">Custom Dashboards</h3>
                                <p className="text-gray-600">Real-time visibility into your operations with custom reporting and analytics dashboards.</p>
                            </div>
                            <div className="bg-white p-6 rounded-lg shadow-lg">
                                <h3 className="text-xl font-semibold text-claravio-blue-900 mb-4">API Development</h3>
                                <p className="text-gray-600">Build robust APIs to enable system communication and third-party integrations.</p>
                            </div>
                            <div className="bg-white p-6 rounded-lg shadow-lg">
                                <h3 className="text-xl font-semibold text-claravio-blue-900 mb-4">Database Solutions</h3>
                                <p className="text-gray-600">Design and optimize databases for performance, scalability, and data integrity.</p>
                            </div>
                            <div className="bg-white p-6 rounded-lg shadow-lg">
                                <h3 className="text-xl font-semibold text-claravio-blue-900 mb-4">Legacy Modernization</h3>
                                <p className="text-gray-600">Update and modernize legacy systems to improve performance and maintainability.</p>
                            </div>
                        </div>
                    </div>

                    {/* Process Section */}
                    <div className="bg-white rounded-lg p-8 shadow-lg mb-16">
                        <h2 className="text-3xl font-bold text-claravio-blue-900 text-center mb-12">Our Development Process</h2>
                        <div className="grid md:grid-cols-4 gap-8">
                            <div className="text-center">
                                <div className="bg-claravio-blue-900 text-white w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <span className="text-xl font-bold">1</span>
                                </div>
                                <h3 className="font-semibold text-claravio-blue-900 mb-2">Discovery</h3>
                                <p className="text-gray-600">Understand your business requirements and current processes</p>
                            </div>
                            <div className="text-center">
                                <div className="bg-claravio-blue-900 text-white w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <span className="text-xl font-bold">2</span>
                                </div>
                                <h3 className="font-semibold text-claravio-blue-900 mb-2">Design</h3>
                                <p className="text-gray-600">Create detailed specifications and system architecture</p>
                            </div>
                            <div className="text-center">
                                <div className="bg-claravio-blue-900 text-white w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <span className="text-xl font-bold">3</span>
                                </div>
                                <h3 className="font-semibold text-claravio-blue-900 mb-2">Develop</h3>
                                <p className="text-gray-600">Build and test your custom software solution</p>
                            </div>
                            <div className="text-center">
                                <div className="bg-claravio-blue-900 text-white w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <span className="text-xl font-bold">4</span>
                                </div>
                                <h3 className="font-semibold text-claravio-blue-900 mb-2">Deploy</h3>
                                <p className="text-gray-600">Implement and provide ongoing support and maintenance</p>
                            </div>
                        </div>
                    </div>

                    {/* CTA Section */}
                    <div className="text-center bg-claravio-blue-900 text-white p-12 rounded-lg">
                        <h2 className="text-3xl font-bold mb-4">Ready to Transform Your Operations?</h2>
                        <p className="text-xl mb-8">Let's discuss how custom software can drive efficiency in your business.</p>
                        <Link to="/contact" className="bg-white text-claravio-blue-900 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition duration-300">
                            Contact Us Today
                        </Link>
                    </div>
                </div>
            </div>
            <Footer />
        </>
    );
};

export default CustomSoftware;